import { type FC, useMemo } from 'react';

import { type IconProps } from '@vakantiesnl/components/src/atoms/Icon';
import { mapAirportName } from '@vakantiesnl/services/src/contentful/mapper';
import { useGetDaysText, useGetNightsText } from '@vakantiesnl/services/src/hooks/utils';
import { type MicroCopy, type Search } from '@vakantiesnl/types';
import dayjs from 'dayjs';

import { useCustomStyles } from './OfferDetails.style';
import { OfferItem } from './OfferItem';

type DepartureAirport = Search.OfferItem['outbound']['departureAirport'];
type HasTransfer = Search.OfferItem['rooms'][number]['hasTransfer'][];
type MealPlan = Search.OfferItem['rooms'][number]['board']['code'];

type Props = {
	microCopies: MicroCopy;
	offer: Pick<Search.OfferItem, 'durationDays' | 'durationNights' | 'departureDate' | 'hasLuggageIncluded'> & {
		departureAirport: DepartureAirport;
		hasTransfer: HasTransfer;
		mealPlanCode: MealPlan;
		roomName?: string;
		isAccoOnly?: boolean;
	};
	className?: string;
	iconSize?: IconProps['size'];
};

export const hasOfferData = (data: Partial<Props['offer']>): data is Props['offer'] => {
	return !Object.values(data).some((value) => typeof value === 'undefined');
};

export const OfferDetails: FC<Props> = ({ microCopies, offer, className, iconSize }) => {
	const { classes, cx } = useCustomStyles();

	const daysText = useGetDaysText(offer.durationDays, false, microCopies);
	const nightsText = useGetNightsText(offer.durationNights, microCopies);

	const departureDateText = useMemo(
		() =>
			`${dayjs(offer.departureDate, 'YYYY-MM-DD').format('dd D MMM YYYY')} (${daysText}${nightsText ? `, ${nightsText}` : ''})`,
		[offer.departureDate, daysText, nightsText],
	);

	return (
		<div className={cx(classes.offerDetails, className)}>
			<OfferItem
				title={departureDateText}
				iconType="calendar"
				dataCy="OfferDetails-duration"
				iconSize={iconSize}
			/>
			{offer.roomName && (
				<OfferItem title={offer.roomName} iconType="room" dataCy="OfferDetails-room" iconSize={iconSize} />
			)}
			{offer.departureAirport && (
				<OfferItem
					title={`${microCopies['offer.flightFrom']} ${mapAirportName(offer.departureAirport, microCopies)}`}
					iconType="airplane"
					dataCy="OfferDetails-outboundFlight"
					iconSize={iconSize}
				/>
			)}
			{offer.hasTransfer?.some(Boolean) && (
				<OfferItem
					title={`${microCopies['common.incl.transfer'].charAt(0).toUpperCase()}${microCopies[
						'common.incl.transfer'
					].slice(1)}`}
					iconType="transfer"
					dataCy="OfferDetails-transfer"
					iconSize={iconSize}
				/>
			)}

			{offer.hasLuggageIncluded && (
				<OfferItem
					title={`${microCopies['common.incl.luggage'].charAt(0).toUpperCase()}${microCopies[
						'common.incl.luggage'
					].slice(1)}`}
					iconType="luggage"
					dataCy="OfferDetails-luggage"
					iconSize={iconSize}
				/>
			)}
			{offer.mealPlanCode && (
				<OfferItem
					title={microCopies[`mealplan_label.${offer.mealPlanCode}`]}
					iconType="food"
					dataCy="OfferDetails-mealplan"
					iconSize={iconSize}
				/>
			)}
			{!!offer.isAccoOnly && (
				<OfferItem
					iconType="car"
					title={microCopies['accoSummary.ownTransport']}
					dataCy="OfferDetails-own-transport"
					iconSize={iconSize}
				/>
			)}
		</div>
	);
};
